import { useState } from "react";
import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

const FAQS = [
  { q: "What is Roamies?", a: "Roamies runs curated small-group trips and local social events for solo travelers ages 18–45. Everyone arrives alone — the point is leaving with real friendships." },
  { q: "Do I have to come alone?", a: "Many Roamies arrive solo — that's what makes meeting your crew part of the adventure. Some trips welcome a friend too; each trip page states its own policy." },
  { q: "How big are the groups?", a: "Small on purpose. Trips are capped (typically 10–12 travelers) and IRL events have their own caps per event. You'll see the max on every listing." },
  { q: "How does booking work?", a: "Right now it's waitlist-first: join the waitlist for a drop, and you'll get dates, pricing, and booking access before the public. No payment is taken on this site." },
  { q: "What's the difference between a Drop and Roamies IRL?", a: "Drops are multi-day group trips (weekend getaways and flagship international escapes). Roamies IRL is local — dinners, rooftops, beach days in your city — the low-commitment way to meet the community first." },
  { q: "I'm nervous about traveling with strangers.", a: "Everyone is, the first time. That's why the group chat opens weeks before the trip, why every drop has a dedicated host, and why you can start with a two-hour IRL event before a three-day trip." },
  { q: "What ages can join?", a: "18–45, confirmed at waitlist signup. It keeps the crew in the same life stage." },
  { q: "Are flights included?", a: "Only when a trip page explicitly says so. Every trip lists exactly what's included and what's not — read that section before joining." },
  { q: "Can I vote on where the next drop goes?", a: "Yes — the Coming Next section on the homepage has a vote button on every future destination. Votes genuinely influence what we plan next." },
];

export default function FaqPage() {
  useSEO("FAQ — Roamies", "Everything about Roamies small-group trips for solo travelers 18–45: how drops work, group sizes, ages, booking, and Roamies IRL events.");
  const [open, setOpen] = useState(null);

  return (
    <div className="py-16 sm:py-24" data-testid="faq-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">FAQ</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]" data-testid="faq-heading">
            Everything people<br />actually ask.
          </h1>
          <div className="mt-12 border-t border-ink/10">
            {FAQS.map((faq, i) => (
              <div key={i} className="border-b border-ink/10" data-testid={`faq-item-${i}`}>
                <button
                  onClick={() => setOpen(open === i ? null : i)}
                  data-testid={`faq-toggle-${i}`}
                  className="w-full flex items-center justify-between py-5 text-left font-bold text-sm uppercase tracking-wide hover:text-flame transition-colors"
                >
                  {faq.q}
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open === i ? "rotate-180 text-flame" : ""}`} />
                </button>
                {open === i && <p className="pb-6 text-sm text-ink/70 leading-relaxed">{faq.a}</p>}
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
